/**
 * VEDANGJYOTISHAM V6.0 — AI QUESTION MAPPER & NATURAL LANGUAGE UNDERSTANDING LAYER
 * 
 * Maps free-form natural language queries (English, Hindi, Hinglish, colloquial phrasing)
 * to Canonical Question Intents and their associated Analytical Contracts in the Jyotish core.
 */

import { LifeThemeId } from '../../types/advancedLifeAnalysis';
import { CanonicalIntentId, AIQuestionMappingResult, QuestionIntent } from '../../types/lifeDomainQuestions';

export interface IntentVariantRule {
  canonicalIntent: CanonicalIntentId;
  domainId: LifeThemeId;
  questionId: string;
  canonicalQuestionEn: string;
  canonicalQuestionHi: string;
  intentType: QuestionIntent;
  keywords: string[];
  patterns: RegExp[];
  hinglishVariants: string[];
}

export const CANONICAL_INTENT_RULES: IntentVariantRule[] = [
  // ==========================================================================
  // MARRIAGE & PARTNERSHIP (marriage_relationship)
  // ==========================================================================
  {
    canonicalIntent: 'MARRIAGE_TIMING',
    domainId: 'marriage_relationship',
    questionId: 'q_marriage_timing',
    canonicalQuestionEn: 'When is a good time for marriage?',
    canonicalQuestionHi: 'विवाह हेतु सर्वाधिक अनुकूल समय कौन सा है?',
    intentType: 'TIMING',
    keywords: ['marry', 'marriage', 'wedding', 'shaadi', 'shadi', 'vivaha', 'byah', 'timing', 'when marry', 'when wedding', 'shaadi kab'],
    patterns: [
      /shaadi\s+(kab|hogi|ka\s+time|ka\s+yog)/i,
      /when\s+(will|should|can|is)\s+(i|my)?\s*(marry|marriage|wedding|get married)/i,
      /(marriage|wedding|shaadi)\s+(timing|time|date|year|age|window|period)/i,
      /vivaha\s+(kab|samaya|muhurta|yog)/i,
      /meri\s+shaadi/i,
      /kab\s+hogi\s+shaadi/i
    ],
    hinglishVariants: [
      'shaadi kab hogi',
      'meri shaadi kab hogi',
      'shaadi ka time kab hai',
      'kab shaadi ke yog hain',
      'shaadi kab tak hogi',
      'vivah kab hoga',
      'when will i marry',
      'when is marriage likely',
      'when should i get married',
      'best time for marriage',
      'marriage timing window'
    ]
  },
  {
    canonicalIntent: 'MARRIAGE_NATURE',
    domainId: 'marriage_relationship',
    questionId: 'q_marriage_nature',
    canonicalQuestionEn: 'Love marriage or family-assisted marriage?',
    canonicalQuestionHi: 'प्रेम विवाह अथवा पारिवारिक सहयोग (पारंपरिक) विवाह?',
    intentType: 'COMPARISON',
    keywords: ['love marriage', 'arranged marriage', 'love vs arranged', 'family marriage', 'pasand ki shaadi', 'gandharva'],
    patterns: [
      /love\s+(or|vs|ya)\s+arranged/i,
      /(love|arranged)\s+marriage/i,
      /(pasand|premi|prem)\s+(ki\s+shaadi|vivah)/i,
      /family\s+(assisted|arranged|choice)/i,
      /khud\s+pasand\s+karu/i
    ],
    hinglishVariants: [
      'love marriage hogi ya arranged',
      'love ya arranged marriage',
      'love marriage ke yog hai kya',
      'ghar wale shaadi karwayenge ya love',
      'prem vivah hoga ya parivarik',
      'will i have love or arranged marriage',
      'love marriage or family assisted'
    ]
  },
  {
    canonicalIntent: 'MARRIAGE_FORMATION',
    domainId: 'marriage_relationship',
    questionId: 'q_marriage_formation',
    canonicalQuestionEn: 'How is marriage likely to come about?',
    canonicalQuestionHi: 'विवाह का माध्यम अथवा संयोग किस प्रकार बनने की संभावना है?',
    intentType: 'NATURE',
    keywords: ['how meet', 'meeting partner', 'meet spouse', 'milna', 'kaise milega', 'shaadi kaise hogi', 'matchmaking'],
    patterns: [
      /how\s+(will|do|can)\s+i\s+(meet|find)\s+(my\s+)?(spouse|husband|wife|partner)/i,
      /partner\s+se\s+kaise\s+milenge/i,
      /how\s+marriage\s+come\s+about/i,
      /meeting\s+circumstances/i,
      /kaha\s+mulakat\s+hogi/i
    ],
    hinglishVariants: [
      'partner se kaise milunga',
      'spouse se mulakat kaise hogi',
      'how will i meet my life partner',
      'where will i meet my spouse',
      'shaadi ka rasta kaisa hoga',
      'how is marriage likely to happen'
    ]
  },
  {
    canonicalIntent: 'PARTNER_CHARACTERISTICS',
    domainId: 'marriage_relationship',
    questionId: 'q_partner_characteristics',
    canonicalQuestionEn: 'What kind of partner is indicated?',
    canonicalQuestionHi: 'कुंडली के अनुसार जीवनसाथी के क्या गुण, स्वभाव व लक्षण दिखाई देते हैं?',
    intentType: 'PARTNER_DYNAMICS',
    keywords: ['partner nature', 'spouse qualities', 'husband nature', 'wife nature', 'partner kaisa hoga', 'jeevansathi'],
    patterns: [
      /(what|how)\s+(kind|type|nature|qualities)\s+of\s+(partner|spouse|husband|wife)/i,
      /partner\s+(kaisa|kaisi)\s+(hoga|hogi)/i,
      /jeevansathi\s+ka\s+swabhav/i,
      /spouse\s+characteristics/i,
      /future\s+husband\s+wife\s+qualities/i
    ],
    hinglishVariants: [
      'meri wife kaisi hogi',
      'mera husband kaisa hoga',
      'life partner ka nature kaisa hoga',
      'spouse qualities in kundali',
      'what will my partner be like',
      'partner temperament in chart'
    ]
  },
  {
    canonicalIntent: 'MARRIAGE_STRENGTHS_CHALLENGES',
    domainId: 'marriage_relationship',
    questionId: 'q_marriage_strengths_challenges',
    canonicalQuestionEn: 'What are the major strengths and challenges in marriage?',
    canonicalQuestionHi: 'वैवाहिक जीवन की मुख्य शक्तियां एवं सावधानियां क्या हैं?',
    intentType: 'STABILITY',
    keywords: ['marital stability', 'marriage friction', 'strengths challenges', 'shadi me problem', 'daampatya sukh'],
    patterns: [
      /(strengths|challenges|problems|friction|harmony)\s+in\s+(marriage|relationship)/i,
      /marital\s+(stability|issues|harmony|longevity)/i,
      /shaadi\s+me\s+tanaav/i,
      /daampatya\s+sukh/i
    ],
    hinglishVariants: [
      'shaadi me koi problem to nahi hogi',
      'marital life me kya challenge hai',
      'marriage stability kaisi rahegi',
      'major strengths and challenges in marriage',
      'how to maintain marital harmony'
    ]
  },
  {
    canonicalIntent: 'CURRENT_RELATIONSHIP_STATE',
    domainId: 'marriage_relationship',
    questionId: 'q_relationship_current_state',
    canonicalQuestionEn: 'What is happening in my relationship now?',
    canonicalQuestionHi: 'वर्तमान में मेरे प्रेम व संबंध जीवन में क्या ग्रह-प्रभाव सक्रिय हैं?',
    intentType: 'CURRENT_STATE',
    keywords: ['relationship now', 'current relationship', 'ab kya chal raha hai', 'relationship status today'],
    patterns: [
      /what\s+is\s+happening\s+in\s+my\s+relationship\s+now/i,
      /relationship\s+(current\s+state|status\s+today|phase\s+now)/i,
      /relationship\s+me\s+ab\s+kya\s+chal\s+raha\s+hai/i,
      /current\s+love\s+life/i
    ],
    hinglishVariants: [
      'relationship me abhi kya chal raha hai',
      'current relationship phase',
      'what is happening in relationship today',
      'abhi love life kaisi hai'
    ]
  },
  {
    canonicalIntent: 'RELATIONSHIP_FUTURE_PERIODS',
    domainId: 'marriage_relationship',
    questionId: 'q_relationship_future_periods',
    canonicalQuestionEn: 'What important relationship periods lie ahead?',
    canonicalQuestionHi: 'आगामी समय में संबंध एवं दांपत्य के महत्वपूर्ण काल-खंड कौन से हैं?',
    intentType: 'TIMING',
    keywords: ['relationship future', 'upcoming love period', 'future marriage periods', 'aage ka samay'],
    patterns: [
      /(important|upcoming|future)\s+(relationship|love|marriage)\s+(periods|times|windows)/i,
      /aage\s+ka\s+relationship\s+samay/i,
      /future\s+relationship\s+timeline/i
    ],
    hinglishVariants: [
      'relationship me aage kya hone wala hai',
      'upcoming relationship periods',
      'future love and marriage timing',
      'aane wale relationship yog'
    ]
  },
  {
    canonicalIntent: 'RELATIONSHIP_TO_MARRIAGE',
    domainId: 'marriage_relationship',
    questionId: 'q_relationship_to_marriage',
    canonicalQuestionEn: 'Is my current relationship moving toward marriage?',
    canonicalQuestionHi: 'क्या वर्तमान प्रेम संबंध विवाह में परिणत होने की दिशा में अग्रसर है?',
    intentType: 'POSSIBILITY',
    keywords: ['relationship to marriage', 'will relationship turn into marriage', 'convert to marriage', 'shaadi me badlega'],
    patterns: [
      /is\s+(my\s+)?(current\s+)?relationship\s+moving\s+toward\s+marriage/i,
      /will\s+(we|my\s+relationship)\s+(get\s+married|turn\s+into\s+marriage)/i,
      /kya\s+ye\s+relationship\s+shaadi\s+me\s+badlega/i,
      /current\s+partner\s+se\s+shaadi\s+hogi/i
    ],
    hinglishVariants: [
      'kya meri current relationship shaadi me badlegi',
      'will my relationship convert to marriage',
      'current boyfriend girlfriend se shaadi hogi kya',
      'is my relationship heading to marriage'
    ]
  },
  {
    canonicalIntent: 'RELATIONSHIP_TRANSITIONS',
    domainId: 'marriage_relationship',
    questionId: 'q_relationship_transitions',
    canonicalQuestionEn: 'Are significant relationship transitions ahead?',
    canonicalQuestionHi: 'क्या संबंध में कोई महत्वपूर्ण बदलाव अथवा रूपांतरण संभावित है?',
    intentType: 'TRANSITION',
    keywords: ['relationship transition', 'breakup', 'shift in relationship', 'badlav', 'relationship change'],
    patterns: [
      /(significant|major)\s+relationship\s+transitions/i,
      /relationship\s+(shift|transition|pivot|change)/i,
      /relationship\s+me\s+koi\s+bada\s+badlav/i
    ],
    hinglishVariants: [
      'relationship me koi transition hone wala hai kya',
      'are relationship transitions ahead',
      'relationship shift kab aayega'
    ]
  },
  {
    canonicalIntent: 'FAMILY_ROLE_IN_MARRIAGE',
    domainId: 'marriage_relationship',
    questionId: 'q_family_role_in_marriage',
    canonicalQuestionEn: 'What role may family play in my marriage?',
    canonicalQuestionHi: 'विवाह एवं दांपत्य में परिवार व ससुराल पक्ष की क्या भूमिका रहेगी?',
    intentType: 'PARTNER_DYNAMICS',
    keywords: ['family role in marriage', 'in laws', 'family support marriage', 'sasural', 'ghar walo ka role'],
    patterns: [
      /family\s+(role|involvement|support)\s+in\s+marriage/i,
      /in-?laws\s+(relationship|support|dynamics)/i,
      /ghar\s+walo\s+ka\s+kya\s+role\s+hoga/i,
      /sasural\s+se\s+kaisa\s+sambandh/i
    ],
    hinglishVariants: [
      'shaadi me family ka kya role rahega',
      'ghar wale shaadi me support karenge kya',
      'what role will family play in marriage',
      'sasural paksh kaisa rahega'
    ]
  },
  {
    canonicalIntent: 'MARITAL_SEVERANCE_LEGAL',
    domainId: 'marriage_relationship',
    questionId: 'q_marital_severance_specialized',
    canonicalQuestionEn: 'Marital Severance, Alimony & Custody',
    canonicalQuestionHi: 'विवाह विच्छेद, भरण-पोषण (एलिमनी) एवं संतान अभिरक्षा',
    intentType: 'TRANSITION',
    keywords: ['divorce', 'alimony', 'custody', 'separation', 'talaq', 'marital severance', 'legal separation'],
    patterns: [
      /(divorce|separation|alimony|custody|talaq)/i,
      /marital\s+severance/i,
      /alimony\s+and\s+child\s+custody/i,
      /alag\s+hona\s+padd\s+sakta\s+hai/i
    ],
    hinglishVariants: [
      'divorce ke yog hai kya',
      'alimony and custody in kundali',
      'separation se bachne ke upay',
      'marital severance and legal matters'
    ]
  },

  // ==========================================================================
  // CAREER & ENTERPRISE (career_job)
  // ==========================================================================
  {
    canonicalIntent: 'CAREER_CURRENT_STATE',
    domainId: 'career_job',
    questionId: 'q_career_current_state',
    canonicalQuestionEn: 'What is happening in my career now?',
    canonicalQuestionHi: 'वर्तमान में मेरे कार्यक्षेत्र व आजीविका में क्या स्थिति चल रही है?',
    intentType: 'CURRENT_STATE',
    keywords: ['career now', 'job status now', 'career state', 'naukri me abhi kya chal raha hai', 'professional phase'],
    patterns: [
      /what\s+is\s+happening\s+in\s+(my\s+)?career\s+now/i,
      /current\s+(career|job|professional)\s+(state|phase|status)/i,
      /career\s+me\s+ab\s+kya\s+chal\s+raha\s+hai/i
    ],
    hinglishVariants: [
      'career me abhi kya chal raha hai',
      'job me kya chal raha hai',
      'what is happening in my career today',
      'current career status'
    ]
  },
  {
    canonicalIntent: 'CAREER_JOB_CHANGE_TIMING',
    domainId: 'career_job',
    questionId: 'q_career_job_change_timing',
    canonicalQuestionEn: 'When is a good time for a job change?',
    canonicalQuestionHi: 'नौकरी बदलने अथवा नई नौकरी की तलाश हेतु शुभ समय कौन सा है?',
    intentType: 'TIMING',
    keywords: ['job change', 'switch job', 'new job', 'naukri kab badlu', 'job switch timing', 'career change'],
    patterns: [
      /when\s+(is|should|can)\s+(a\s+good\s+time\s+for\s+)?(job\s+change|switch\s+job|change\s+job|new\s+job)/i,
      /job\s+(switch|change)\s+kab\s+(kare|hoga|karna\s+chahiye)/i,
      /naukri\s+kab\s+badlu/i
    ],
    hinglishVariants: [
      'job switch kab karu',
      'naukri kab badalni chahiye',
      'when should i change my job',
      'when is a good time for job change',
      'new job kab milegi'
    ]
  },
  {
    canonicalIntent: 'CAREER_GROWTH_PERIODS',
    domainId: 'career_job',
    questionId: 'q_career_growth_periods',
    canonicalQuestionEn: 'When are stronger career-growth periods?',
    canonicalQuestionHi: 'कार्यक्षेत्र में पदोन्नति एवं तीव्र उन्नति के प्रबल काल-खंड कब हैं?',
    intentType: 'TIMING',
    keywords: ['promotion timing', 'career growth', 'tarraqi kab hogi', 'appraisal timing', 'career elevation'],
    patterns: [
      /when\s+are\s+(stronger|best|peak)\s+career[- ]growth\s+periods/i,
      /(promotion|appraisal|growth)\s+timing/i,
      /career\s+me\s+promotion\s+kab\s+milega/i,
      /taraqqi\s+kab\s+hogi/i
    ],
    hinglishVariants: [
      'promotion kab milega',
      'career growth kab hogi',
      'when will i get promoted',
      'strongest career growth periods',
      'career me aage badhne ka time'
    ]
  },
  {
    canonicalIntent: 'CAREER_DIRECTION',
    domainId: 'career_job',
    questionId: 'q_career_direction',
    canonicalQuestionEn: 'What career direction is indicated?',
    canonicalQuestionHi: 'कुंडली के अनुसार कौन सा कार्यक्षेत्र अथवा उद्योग सर्वाधिक अनुकूल है?',
    intentType: 'NATURE',
    keywords: ['career direction', 'which field', 'best career for me', 'kaun sa field sahi hai', 'ideal profession'],
    patterns: [
      /what\s+career\s+direction\s+is\s+indicated/i,
      /which\s+(career|field|profession|industry)\s+is\s+(best|suitable|favored)/i,
      /kaun\s+sa\s+career\s+sahi\s+rahega/i
    ],
    hinglishVariants: [
      'kaun sa career field best hai',
      'what career direction is indicated in kundali',
      'which profession suits me best',
      'career ki sahi disha'
    ]
  },
  {
    canonicalIntent: 'EMPLOYMENT_VS_BUSINESS',
    domainId: 'career_job',
    questionId: 'q_employment_vs_business',
    canonicalQuestionEn: 'Employment or entrepreneurship?',
    canonicalQuestionHi: 'नौकरी (सेवा) अथवा स्वतंत्र व्यापार (बिजनेस) में से क्या अधिक फलदायी है?',
    intentType: 'COMPARISON',
    keywords: ['job vs business', 'employment or business', 'naukri ya business', 'startup vs job', 'entrepreneurship or job'],
    patterns: [
      /(employment|job)\s+(or|vs|ya)\s+(entrepreneurship|business|startup)/i,
      /naukri\s+kare\s+ya\s+business/i,
      /job\s+kare\s+ya\s+vyapar/i
    ],
    hinglishVariants: [
      'job karu ya business',
      'naukri sahi hai ya vyapar',
      'employment or entrepreneurship',
      'job vs business in my chart',
      'startup karu ya job'
    ]
  },

  // ==========================================================================
  // COMMERCIAL VENTURE & STARTUPS (business)
  // ==========================================================================
  {
    canonicalIntent: 'BUSINESS_FEASIBILITY',
    domainId: 'business',
    questionId: 'q_business_feasibility',
    canonicalQuestionEn: 'Is entrepreneurship supported in my chart?',
    canonicalQuestionHi: 'क्या मेरी कुंडली में स्वतंत्र व्यापार/स्टार्टअप के योग हैं?',
    intentType: 'POSSIBILITY',
    keywords: ['entrepreneurship supported', 'business yoga', 'startup support', 'vyapar yog in kundali'],
    patterns: [
      /is\s+entrepreneurship\s+supported/i,
      /business\s+karna\s+kaisa\s+rahega/i,
      /kya\s+mere\s+kundali\s+me\s+business\s+yog\s+hai/i
    ],
    hinglishVariants: [
      'kya mai business kar sakta hu',
      'is entrepreneurship supported in my chart',
      'business yoga in my chart',
      'mera khud ka business chalega kya'
    ]
  },
  {
    canonicalIntent: 'BUSINESS_STARTUP_TIMING',
    domainId: 'business',
    questionId: 'q_business_startup_timing',
    canonicalQuestionEn: 'When is a good time to start a business?',
    canonicalQuestionHi: 'नवीन व्यापार अथवा स्टार्टअप प्रारंभ करने हेतु शुभ समय कौन सा है?',
    intentType: 'TIMING',
    keywords: ['start business timing', 'launch startup', 'when start company', 'business shuru kab kare'],
    patterns: [
      /when\s+(is\s+a\s+good\s+time\s+to|should\s+i)\s+(start|launch)\s+a\s+(business|startup|company)/i,
      /business\s+kab\s+start\s+kare/i,
      /startup\s+launch\s+timing/i
    ],
    hinglishVariants: [
      'business kab shuru karu',
      'when is a good time to start a business',
      'startup kab launch karna chahiye',
      'business start date'
    ]
  },

  // ==========================================================================
  // WEALTH & LIQUIDITY (wealth_finance)
  // ==========================================================================
  {
    canonicalIntent: 'WEALTH_CURRENT_FINANCES',
    domainId: 'wealth_finance',
    questionId: 'q_wealth_current_finances',
    canonicalQuestionEn: 'What is happening with my finances now?',
    canonicalQuestionHi: 'वर्तमान में मेरी आर्थिक स्थिति एवं धन संचय में क्या प्रभाव सक्रिय हैं?',
    intentType: 'CURRENT_STATE',
    keywords: ['finances now', 'current money state', 'financial situation today', 'paisa ka hal'],
    patterns: [
      /what\s+is\s+happening\s+with\s+(my\s+)?finances\s+now/i,
      /current\s+(financial|wealth|money)\s+(state|phase|status)/i,
      /paise\s+ki\s+kya\s+sthiti\s+hai/i
    ],
    hinglishVariants: [
      'financial condition abhi kaisi hai',
      'what is happening with my finances now',
      'paise ki sthiti abhi kaisi hai',
      'current wealth phase'
    ]
  },
  {
    canonicalIntent: 'WEALTH_ASSET_BUILDING',
    domainId: 'wealth_finance',
    questionId: 'q_wealth_asset_building',
    canonicalQuestionEn: 'When are major asset-building periods & stock market investments favored?',
    canonicalQuestionHi: 'दीर्घकालीन परिसंपत्ति निर्माण एवं शेयर बाजार/इक्विटी निवेश के अनुकूल समय क्या हैं?',
    intentType: 'TIMING',
    keywords: [
      'asset building',
      'stock market',
      'stocks',
      'shares',
      'share market',
      'equity investment',
      'investment',
      'investing',
      'stock trading',
      'crypto',
      'mutual funds',
      'portfolio'
    ],
    patterns: [
      /(asset[- ]building|stock(\s+market)?|equity|shares?(\s+market)?|investments?|trading)/i,
      /stock\s+market\s+(investment|kaisa|kab)/i,
      /share\s+market\s+me\s+invest/i,
      /stock\s+trading\s+kaisa\s+rahega/i
    ],
    hinglishVariants: [
      'stock market investment kaisa rahega',
      'stock market me investment kaisa rahega',
      'share market trading in kundali',
      'asset building periods',
      'when to invest in stocks and equity',
      'share market me paisa lagana chahiye'
    ]
  },

  // ==========================================================================
  // CHILDREN & LEGACY (children)
  // ==========================================================================
  {
    canonicalIntent: 'CHILDREN_PROGENY_TIMING',
    domainId: 'children',
    questionId: 'q_children_progeny_timing',
    canonicalQuestionEn: 'When are children-related periods activated?',
    canonicalQuestionHi: 'संतान प्राप्ति, गर्भाधान एवं पितृत्व/मातृत्व हेतु अनुकूल समय कौन सा है?',
    intentType: 'TIMING',
    keywords: ['children timing', 'child birth date', 'santan prapti', 'pregnancy timing', 'parenthood'],
    patterns: [
      /when\s+are\s+children[- ]related\s+periods\s+activated/i,
      /(pregnancy|child\s+birth|conception|parenthood)\s+timing/i,
      /santan\s+(prapti|yog|kab\s+hogi)/i
    ],
    hinglishVariants: [
      'santan prapti kab hogi',
      'baby kab hoga',
      'when will we have a child',
      'children timing window in chart'
    ]
  },

  // ==========================================================================
  // HIGHER EDUCATION & EXAMS (education)
  // ==========================================================================
  {
    canonicalIntent: 'EDUCATION_EXAMS_STUDY_PERIODS',
    domainId: 'education',
    questionId: 'q_education_exams_study_periods',
    canonicalQuestionEn: 'When are stronger study or competitive exam periods?',
    canonicalQuestionHi: 'प्रतियोगी परीक्षाओं, प्रवेश एवं अध्ययन में सफलता के प्रबल काल-खंड कब हैं?',
    intentType: 'TIMING',
    keywords: ['competitive exams', 'upsc exam', 'higher studies', 'exam success', 'pariksha kab niklegi'],
    patterns: [
      /(competitive\s+exams?|upsc|higher\s+education|exam\s+success)\s+(periods|timing)/i,
      /exam\s+kab\s+clear\s+hoga/i,
      /padhai\s+me\s+safalta\s+kab/i
    ],
    hinglishVariants: [
      'competitive exam kab clear hoga',
      'exam success timing',
      'higher education ke yog',
      'padhai me success kab milegi'
    ]
  },

  // ==========================================================================
  // PROPERTY & INHERITANCE (house_property)
  // ==========================================================================
  {
    canonicalIntent: 'PROPERTY_BUY_TIMING',
    domainId: 'house_property',
    questionId: 'q_property_buy_timing',
    canonicalQuestionEn: 'When is a good time to buy property?',
    canonicalQuestionHi: 'भूमि, भवन, फ्लैट अथवा अचल संपत्ति क्रय करने हेतु शुभ समय कौन सा है?',
    intentType: 'TIMING',
    keywords: ['buy property', 'buy house', 'makaan kab banega', 'real estate timing', 'ghar kharidna'],
    patterns: [
      /when\s+(is\s+a\s+good\s+time\s+to|should\s+i)\s+buy\s+(property|house|flat|land|home)/i,
      /ghar\s+kab\s+(banega|kharidenge)/i,
      /property\s+purchase\s+timing/i
    ],
    hinglishVariants: [
      'ghar kab khareedu',
      'makaan kab banega',
      'when can i buy a house',
      'property buy timing in kundali'
    ]
  },

  // ==========================================================================
  // VEHICLES & CONVEYANCES (vehicle)
  // ==========================================================================
  {
    canonicalIntent: 'VEHICLE_BUY_TIMING',
    domainId: 'vehicle',
    questionId: 'q_vehicle_buy_timing',
    canonicalQuestionEn: 'When is a good time to buy a vehicle?',
    canonicalQuestionHi: 'नवीन वाहन अथवा सवारी साधन क्रय करने हेतु शुभ समय कौन सा है?',
    intentType: 'TIMING',
    keywords: ['buy vehicle', 'buy car', 'gaadi kab lu', 'car purchase timing', 'vahana sukha'],
    patterns: [
      /when\s+(is\s+a\s+good\s+time\s+to|should\s+i)\s+buy\s+a\s+(vehicle|car|bike|automobile)/i,
      /gaadi\s+kab\s+(khareedu|lenge)/i,
      /car\s+purchase\s+timing/i
    ],
    hinglishVariants: [
      'car kab khareedu',
      'gaadi lene ka shubh samay',
      'when to buy a new car',
      'vehicle purchase window'
    ]
  },

  // ==========================================================================
  // HEALTH & VITALITY (health_vitality) (Responsible Astrology)
  // ==========================================================================
  {
    canonicalIntent: 'HEALTH_WELLBEING_CYCLE',
    domainId: 'health_vitality',
    questionId: 'q_health_wellbeing_cycle',
    canonicalQuestionEn: 'What is my current wellbeing and vitality cycle astrologically?',
    canonicalQuestionHi: 'वर्तमान में आरोग्य, शारीरिक ऊर्जा एवं जीवनशैली के कौन से ग्रह-प्रभाव सक्रिय हैं?',
    intentType: 'CURRENT_STATE',
    keywords: ['health vitality', 'wellbeing cycle', 'swasthya kaisa rahega', 'energy levels', 'ayurvedic lifestyle'],
    patterns: [
      /what\s+is\s+my\s+current\s+wellbeing\s+cycle/i,
      /(health|vitality|energy)\s+(themes|status|cycle|timing)/i,
      /swasthya\s+kaisa\s+rahega/i
    ],
    hinglishVariants: [
      'health kaisi rahegi',
      'swasthya ka haal kundali me',
      'current wellbeing cycle',
      'vitality and stamina in chart'
    ]
  },

  // ==========================================================================
  // FOREIGN PR & TRAVEL (foreign_travel)
  // ==========================================================================
  {
    canonicalIntent: 'FOREIGN_RESIDENCE_PR_SUPPORT',
    domainId: 'foreign_travel',
    questionId: 'q_foreign_residence_pr_support',
    canonicalQuestionEn: 'Is foreign residence / permanent residency (PR) supported?',
    canonicalQuestionHi: 'क्या कुंडली में विदेश गमन, स्थायी निवास (पीआर) एवं अंतर्राष्ट्रीय प्रवास के योग हैं?',
    intentType: 'POSSIBILITY',
    keywords: ['foreign PR', 'settle abroad', 'videsh yatra', 'visa approval', 'foreign travel'],
    patterns: [
      /is\s+foreign\s+residence\s+supported/i,
      /(permanent\s+residency|pr|citizenship)\s+(abroad|foreign|chances)/i,
      /videsh\s+(me\s+settle|kab\s+jayenge|pr\s+milega)/i
    ],
    hinglishVariants: [
      'videsh settle hone ke yog hai kya',
      'foreign PR kab milega',
      'will i settle abroad',
      'visa approval and international relocation'
    ]
  },
  {
    canonicalIntent: 'RELOCATION_TIMING',
    domainId: 'foreign_travel',
    questionId: 'q_foreign_relocation_activation',
    canonicalQuestionEn: 'When is a major relocation or residence change activated?',
    canonicalQuestionHi: 'स्थान परिवर्तन अथवा नए नगर/देश में स्थानांतरण के सक्रिय समय क्या हैं?',
    intentType: 'TIMING',
    keywords: ['relocation timing', 'when will i move', 'when relocate', 'shift house', 'sthan parivartan', 'shift kab hoga'],
    patterns: [
      /when\s+(will|can|should)\s+i\s+(move|relocate|shift)/i,
      /(relocation|moving|shifting)\s+(timing|date|year|window)/i,
      /sthan\s+parivartan\s+kab/i,
      /shift\s+kab\s+hoga/i
    ],
    hinglishVariants: [
      'when will i move',
      'relocation timing in kundali',
      'sthan parivartan kab hoga',
      'ghar kab shift hoga',
      'when will i relocate'
    ]
  },
  {
    canonicalIntent: 'RELOCATION_DIRECTION',
    domainId: 'foreign_travel',
    questionId: 'q_foreign_relocation_activation',
    canonicalQuestionEn: 'Which direction is indicated for career or residence relocation?',
    canonicalQuestionHi: 'आजीविका अथवा निवास परिवर्तन हेतु कौन सी दिशा सर्वाधिक शुभ व अनुकूल है?',
    intentType: 'NATURE',
    keywords: ['which direction', 'relocation direction', 'moving direction', 'kis disha me jaye', 'favorable direction'],
    patterns: [
      /which\s+direction\s+(will|should|is)\s+(i\s+)?(move|relocate|favorable)/i,
      /kis\s+disha\s+(me\s+jaye|hogi|faydemand)/i,
      /relocation\s+direction/i
    ],
    hinglishVariants: [
      'which direction will i move',
      'kis disha me shift hona chahiye',
      'direction of relocation in astrology',
      'which side is favorable for relocation'
    ]
  },
  {
    canonicalIntent: 'RELOCATION_HOMETOWN_RETURN',
    domainId: 'foreign_travel',
    questionId: 'q_foreign_relocation_activation',
    canonicalQuestionEn: 'Is a return or reconnection with birthplace / hometown indicated?',
    canonicalQuestionHi: 'क्या जन्मभूमि अथवा गृह नगर वापसी/पुनरागमन के योग कुंडली में उपस्थित हैं?',
    intentType: 'POSSIBILITY',
    keywords: ['return to hometown', 'back to birthplace', 'ghar wapsi', 'hometown return', 'janmabhoomi return'],
    patterns: [
      /(return|go\s+back|move\s+back)\s+(to\s+)?(hometown|birthplace|home|delhi|mumbai)/i,
      /ghar\s+wapsi\s+ke\s+yog/i,
      /apne\s+shehar\s+wapas\s+kab/i
    ],
    hinglishVariants: [
      'will i go back to my hometown',
      'ghar wapsi kab hogi',
      'return to birthplace in kundali',
      'will i move back to my hometown'
    ]
  },

  // ==========================================================================
  // PROTECTION & COURT CASES (litigation_competition)
  // ==========================================================================
  {
    canonicalIntent: 'LEGAL_ACTIVE_THEMES',
    domainId: 'litigation_competition',
    questionId: 'q_legal_active_themes',
    canonicalQuestionEn: 'Are legal or dispute-related themes currently active, and what protects procedural stability?',
    canonicalQuestionHi: 'कानूनी, विधिक एवं प्रक्रियात्मक सतर्कता के क्या योग हैं, और क्या सुरक्षा उपाय प्रभावी हैं?',
    intentType: 'CHALLENGE',
    keywords: ['court case', 'legal dispute', 'shatru hanta', 'court me jeet', 'litigation protection'],
    patterns: [
      /(legal|dispute|court\s+case|litigation)\s+(themes|active|victory|protection)/i,
      /court\s+case\s+me\s+kya\s+hoga/i,
      /shatru\s+hanta\s+yoga/i
    ],
    hinglishVariants: [
      'court case me jeet kab milegi',
      'legal case me kya outcome hoga',
      'are legal dispute themes active',
      'litigation protection in kundali'
    ]
  },

  // ==========================================================================
  // GURU & SELF-REALIZATION (spirituality)
  // ==========================================================================
  {
    canonicalIntent: 'SPIRITUALITY_ACTIVE_THEMES',
    domainId: 'spirituality',
    questionId: 'q_spirituality_active_themes',
    canonicalQuestionEn: 'What spiritual, meditation, and inner-growth themes are currently active?',
    canonicalQuestionHi: 'वर्तमान में साधना, ध्यान, गुरु कृपा एवं आत्म-विकास के कौन से आध्यात्मिक योग सक्रिय हैं?',
    intentType: 'CURRENT_STATE',
    keywords: ['spiritual growth', 'meditation timing', 'guru diksha', 'adhyatmik unnati', 'moksha trikona', 'spiritual themes active'],
    patterns: [
      /(spiritual|meditation|inner[- ]growth|guru)\s+(themes|timing|active)/i,
      /what\s+is\s+spiritually\s+active/i,
      /adhyatmik\s+unnati\s+kab\s+hogi/i,
      /current\s+spiritual\s+state/i
    ],
    hinglishVariants: [
      'adhyatmik unnati kab hogi',
      'guru diksha ka shubh samay',
      'spiritual growth in kundali',
      'meditation and sadhana phase',
      'what is spiritually active now'
    ]
  },
  {
    canonicalIntent: 'SPIRITUAL_PATH',
    domainId: 'spirituality',
    questionId: 'q_spiritual_path_inquiry',
    canonicalQuestionEn: 'What is there for me spiritually and what path suits me?',
    canonicalQuestionHi: 'मेरे लिए आध्यात्मिक मार्ग पर क्या संकेत हैं और कौन सा पथ अनुकूल है?',
    intentType: 'POSSIBILITY',
    keywords: ['spiritual path', 'what is there for me spiritually', 'spiritual orientation', 'bhakti or jnana', 'adhyatmik marg'],
    patterns: [
      /what\s+is\s+there\s+for\s+me\s+spiritually/i,
      /what\s+(is\s+my\s+)?spiritual\s+(path|journey|orientation)/i,
      /mera\s+adhyatmik\s+marg/i,
      /bhakti\s+ya\s+gyan\s+marg/i
    ],
    hinglishVariants: [
      'what is there for me spiritually',
      'mera spiritual path kya hai',
      'what spiritual path suits me',
      'bhakti yoga ya dhyan yoga'
    ]
  },
  {
    canonicalIntent: 'SPIRITUAL_PRACTICE',
    domainId: 'spirituality',
    questionId: 'q_spirituality_meditation_sadhana',
    canonicalQuestionEn: 'Which spiritual practice (Sādhanā) resonates best with my chart?',
    canonicalQuestionHi: 'मेरी कुंडली के अनुसार कौन सी साधना या ध्यान पद्धति सर्वाधिक अनुकूल है?',
    intentType: 'DEVELOPMENT',
    keywords: ['sadhana', 'meditation practice', 'mantra japa', 'svadhyaya', 'kaunsi sadhana kare'],
    patterns: [
      /which\s+spiritual\s+practice/i,
      /what\s+(sadhana|meditation)\s+suits\s+me/i,
      /kaunsi\s+sadhana\s+kare/i,
      /dhyan\s+ya\s+mantra\s+jap/i
    ],
    hinglishVariants: [
      'which spiritual practice suits me',
      'what sadhana should i do',
      'kaunsa dhyan sahi rahega',
      'best meditation technique for me'
    ]
  },
  {
    canonicalIntent: 'DHARMA_MOKSHA_BALANCE',
    domainId: 'spirituality',
    questionId: 'q_spirituality_career_balance',
    canonicalQuestionEn: 'How do worldly responsibilities and spiritual growth interact in my life?',
    canonicalQuestionHi: 'सांसारिक उत्तरदायित्वों एवं आध्यात्मिक विकास में कैसा संतुलन रहेगा?',
    intentType: 'POSSIBILITY',
    keywords: ['spirituality in career', 'dharma moksha balance', 'material life and spirituality', 'corporate me spirituality'],
    patterns: [
      /spirituality\s+(and|vs|in)\s+(career|work|material\s+life)/i,
      /can\s+i\s+be\s+spiritual\s+while\s+working/i,
      /dharma\s+moksha\s+balance/i
    ],
    hinglishVariants: [
      'can i be spiritual while working in job',
      'corporate me spirituality possible hai kya',
      'how to balance dharma and moksha',
      'worldly responsibility and spiritual path'
    ]
  },
  {
    canonicalIntent: 'GURU_TRADITION',
    domainId: 'spirituality',
    questionId: 'q_spirituality_guru_guidance',
    canonicalQuestionEn: 'What Guru, mentorship, and sacred guidance themes are indicated?',
    canonicalQuestionHi: 'सद्गुरु मिलन, दीक्षा प्राप्ति अथवा उच्च आध्यात्मिक मार्गदर्शन के क्या योग हैं?',
    intentType: 'POSSIBILITY',
    keywords: ['guru diksha', 'spiritual master', 'guru guidance', 'spiritual mentor', 'guru kab milenge'],
    patterns: [
      /will\s+i\s+(find|get)\s+a\s+guru/i,
      /guru\s+diksha\s+in\s+chart/i,
      /spiritual\s+(mentor|guide|teacher)/i,
      /guru\s+kab\s+milenge/i
    ],
    hinglishVariants: [
      'guru kab milenge',
      'will i get a spiritual guru',
      'guru diksha yog in kundali',
      'spiritual mentor guidance'
    ]
  },

  // ==========================================================================
  // SIBLINGS & FAMILY BOND (family_parents)
  // ==========================================================================
  {
    canonicalIntent: 'FAMILY_ACTIVE_THEMES',
    domainId: 'family_parents',
    questionId: 'q_family_active_themes',
    canonicalQuestionEn: 'What family harmony, kinship duties, and sibling relationship themes are active?',
    canonicalQuestionHi: 'वर्तमान में पारिवारिक सौहार्द, कुल दायित्व एवं भाई-बहनों के संबंधों में क्या ग्रह-प्रभाव सक्रिय हैं?',
    intentType: 'CURRENT_STATE',
    keywords: ['family harmony', 'sibling relations', 'bhai behen ka sambandh', 'parivarik shanti'],
    patterns: [
      /family\s+(harmony|themes|duties|relationships?)/i,
      /sibling\s+(relations|support|dynamics)/i,
      /parivarik\s+shanti\s+aur\s+sambandh/i
    ],
    hinglishVariants: [
      'family me shanti kaise rahegi',
      'siblings ke sath relation kaisa rahega',
      'what family harmony themes are active',
      'bhai behen ke sath sambandh'
    ]
  }
];

/**
 * Detect language of query (English, Hindi, or Hinglish)
 */
export function detectQueryLanguage(query: string): 'ENGLISH' | 'HINDI' | 'HINGLISH' {
  const hindiRegex = /[\u0900-\u097F]/;
  if (hindiRegex.test(query)) {
    return 'HINDI';
  }

  const hinglishMarkers = [
    'kab', 'kaisa', 'kaisi', 'hoga', 'hogi', 'meri', 'mera', 'mere', 'kya',
    'chal', 'raha', 'hai', 'shaadi', 'shadi', 'shuru', 'karu', 'padhai',
    'paise', 'ghar', 'gaadi', 'naukri', 'vyapar', 'santan', 'milega', 'milegi',
    'parivar', 'bhai', 'behen', 'swasthya', 'videsh'
  ];

  const words = query.toLowerCase().split(/\s+/);
  const hasHinglish = words.some(w => hinglishMarkers.includes(w));

  return hasHinglish ? 'HINGLISH' : 'ENGLISH';
}

/**
 * Maps a natural-language query to its Canonical Intent and closest Curated Question
 */
export function mapNaturalLanguageQuestion(
  rawQuery: string,
  preferredDomainId?: LifeThemeId
): AIQuestionMappingResult {
  const clean = rawQuery.trim().toLowerCase();
  const lang = detectQueryLanguage(clean);

  if (!clean) {
    // Default fallback to first intent in domain
    const defaultRule = preferredDomainId
      ? CANONICAL_INTENT_RULES.find(r => r.domainId === preferredDomainId) || CANONICAL_INTENT_RULES[0]
      : CANONICAL_INTENT_RULES[0];

    return {
      rawQuery,
      domainId: defaultRule.domainId,
      canonicalIntent: defaultRule.canonicalIntent,
      matchedQuestionId: defaultRule.questionId,
      canonicalQuestionEn: defaultRule.canonicalQuestionEn,
      canonicalQuestionHi: defaultRule.canonicalQuestionHi,
      intent: defaultRule.intentType,
      confidence: 'LOW',
      confidenceScore: 50,
      detectedLanguage: lang
    };
  }

  let bestRule: IntentVariantRule | null = null;
  let highestScore = 0;
  let matchedVariant: string | undefined;

  for (const rule of CANONICAL_INTENT_RULES) {
    let score = 0;

    // Prefer matching within the currently active domain if specified
    if (preferredDomainId && rule.domainId === preferredDomainId) {
      score += 15;
    }

    // 1. Exact pattern match
    for (const pat of rule.patterns) {
      if (pat.test(clean)) {
        score += 50;
        break;
      }
    }

    // 2. Hinglish variants check
    for (const v of rule.hinglishVariants) {
      if (clean.includes(v) || v.includes(clean)) {
        score += 45;
        matchedVariant = v;
        break;
      }
    }

    // 3. Keyword matching
    for (const kw of rule.keywords) {
      if (clean.includes(kw.toLowerCase())) {
        score += 20;
      }
    }

    if (score > highestScore) {
      highestScore = score;
      bestRule = rule;
    }
  }

  if (!bestRule || highestScore < 20) {
    // Fallback within preferred domain
    const fallback = preferredDomainId
      ? CANONICAL_INTENT_RULES.find(r => r.domainId === preferredDomainId) || CANONICAL_INTENT_RULES[0]
      : CANONICAL_INTENT_RULES[0];

    return {
      rawQuery,
      domainId: fallback.domainId,
      canonicalIntent: fallback.canonicalIntent,
      matchedQuestionId: fallback.questionId,
      canonicalQuestionEn: fallback.canonicalQuestionEn,
      canonicalQuestionHi: fallback.canonicalQuestionHi,
      intent: fallback.intentType,
      confidence: 'LOW',
      confidenceScore: 40,
      detectedLanguage: lang
    };
  }

  const confidence: 'HIGH' | 'MEDIUM' | 'LOW' =
    highestScore >= 50 ? 'HIGH' : highestScore >= 30 ? 'MEDIUM' : 'LOW';

  return {
    rawQuery,
    domainId: bestRule.domainId,
    canonicalIntent: bestRule.canonicalIntent,
    matchedQuestionId: bestRule.questionId,
    canonicalQuestionEn: bestRule.canonicalQuestionEn,
    canonicalQuestionHi: bestRule.canonicalQuestionHi,
    intent: bestRule.intentType,
    confidence,
    confidenceScore: Math.min(100, Math.round(highestScore * 1.3)),
    detectedLanguage: lang,
    matchedVariant
  };
}
